"use client";

import { useUser } from "@clerk/nextjs";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import "./signin.css";
import "../../globals.css";
import { Button, CircularProgress, TextField } from "@mui/material";
import Image from "next/image";

export default function LoginPage() {
  const searchParams = useSearchParams();
  const redirectLink = searchParams.get("redirect_url");
  const { isLoaded: isUserLoaded, user } = useUser();
  const year = new Date().getFullYear();
  const [verifying, setVerifying] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(null);
  const [codeError, setCodeError] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [authLoadTimedOut, setAuthLoadTimedOut] = useState(false);
  const [clerkRuntimeStatus, setClerkRuntimeStatus] = useState("hydrating");
  const router = useRouter();

  const getRedirectPath = () => {
    if (!redirectLink) return "/";

    try {
      const redirectUrl = new URL(redirectLink, window.location.origin);
      if (redirectUrl.origin === window.location.origin) {
        return `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`;
      }
    } catch (err) {
      console.log(err);
    }

    return "/";
  };

  useEffect(() => {
    if (isUserLoaded && user) {
      router.replace(getRedirectPath());
    }
  }, [isUserLoaded, user, router, redirectLink]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setAuthLoadTimedOut(true);
    }, 5000);

    const interval = window.setInterval(() => {
      const clerk = window.Clerk;
      if (!clerk) {
        setClerkRuntimeStatus("missing");
        return;
      }

      setClerkRuntimeStatus(clerk.loaded ? "ready" : clerk.status || "loading");
    }, 500);

    return () => {
      window.clearTimeout(timeout);
      window.clearInterval(interval);
    };
  }, []);

  const handleSubmit = async (e) => {
    const clerk = window.Clerk;
    const signIn = clerk?.client?.signIn;

    if (!clerk?.loaded || !signIn) {
      setEmailError("Authentication is still loading. Please try again.");
      return null;
    }

    try {
      // Start the sign-in process using the phone number method
      const { supportedFirstFactors } = await signIn.create({
        identifier: email,
      });

      // Filter the returned array to find the 'phone_code' entry
      const isEmailCodeFactor = (SignInFirstFactor) => {
        return SignInFirstFactor.strategy === "email_code";
      };
      const emailCodeFactor = supportedFirstFactors?.find(isEmailCodeFactor);

      if (emailCodeFactor) {
        // Grab the emailAddressId
        const { emailAddressId } = emailCodeFactor;

        // Send the OTP code to the user
        await signIn.prepareFirstFactor({
          strategy: "email_code",
          emailAddressId,
        });

        // Set verifying to true to display second form
        // and capture the OTP code
        setVerifying(true);
      }
    } catch (err) {
      setEmailError("Please enter a valid email address");
      console.error("Error:", JSON.stringify(err, null, 2));
    }
  };

  const handleSetEmail = (e) => {
    if (e.target.value.trim() !== "") {
      setEmailError("");
    }
    setEmail(e.target.value);
  };

  const handleSetCode = async (e) => {
    setCodeError(null);
    const verificationCode = e.target.value;
    if (verificationCode.length > 5) {
      setLoading(true);
      const clerk = window.Clerk;
      const signIn = clerk?.client?.signIn;

      if (!clerk?.loaded || !signIn) {
        setLoading(false);
        setCodeError("Authentication is still loading. Please try again.");
        return null;
      }

      try {
        // Use the code provided by the user and attempt verification
        const signInAttempt = await signIn.attemptFirstFactor({
          strategy: "email_code",
          code: verificationCode,
        });

        // If verification was completed, set the session to active
        // and redirect the user
        if (signInAttempt.status === "complete") {
          await clerk.setActive({ session: signInAttempt.createdSessionId });

          router.replace(getRedirectPath());
          setLoading(false);
        } else {
          // If the status is not complete, check why. User may need to
          // complete further steps.
          console.error(signInAttempt);
          setLoading(false);
          setCodeError("An error has occurred");
        }
      } catch (err) {
        console.error("Error:", JSON.stringify(err, null, 2));
        setLoading(false);
        if (err.errors?.[0]?.code === "form_code_incorrect") {
          setCodeError("Please enter a valid code");
        }
      }
    }
  };

  if (isUserLoaded && user) {
    return null;
  }
  if (clerkRuntimeStatus !== "ready" || loading) {
    return (
      <div className="flex h-[90dvh] flex-col justify-center items-center m-[auto] gap-4">
        <CircularProgress />
        <p>{loading ? "Completing sign in..." : "Loading authentication..."}</p>
        {clerkRuntimeStatus !== "ready" && authLoadTimedOut ? (
          <div className="max-w-[28rem] text-center text-sm text-gray-600">
            <p>Authentication is taking longer than expected.</p>
            <p>Clerk runtime status: {clerkRuntimeStatus}</p>
          </div>
        ) : null}
      </div>
    );
  }
  return (
    <div className="flex m-[auto] items-start justify-center mt-[6rem]">
      <div className="flex flex-col p-8 gap-4 rounded-lg shadow-xl w-[50%] max-w-[500px] border-solid border-slate-200 border-[1px]">
        <div className="flex justify-center mb-4">
          <div className="relative flex gap-2">
            <Image
              width={150}
              height={150}
              alt="logo"
              src="/Marbury_Logo.png"
            />
            <p className="absolute bottom-[-0.25rem] left-[0.125rem] text-[0.75rem] tracking-[0.25rem] text-[#dc9b28]">
              DIGITAL
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-8">
          <p className="text-xl font-700">Welcome to Marbury Digital</p>
          <p>
            {verifying
              ? "Enter your verification code"
              : "Sign into your account"}
          </p>
        </div>
        {verifying ? (
          <>
            <div className="flex flex-col gap-2">
              <input
                type="otp"
                id="code"
                size="small"
                onChange={handleSetCode}
                autoFocus={true}
                autoComplete="false"
                className="p-2 border-solid border-[1px] border-gray-300 focus:border-[#dc9b28] rounded-md outline-none tracking-[0.5rem] text-center"
              />
              {codeError ? (
                <p className="block text-sm text-red-500">{codeError}</p>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-2">
              <TextField
                type="email"
                id="email"
                label="Enter your email"
                size="small"
                value={email}
                onChange={handleSetEmail}
              />
              {emailError ? (
                <p className="block text-sm text-red-500">{emailError}</p>
              ) : null}
            </div>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disableElevation
              disabled={email.trim() === ""}
            >
              Get Code
            </Button>
            <div className="flex flex-col gap-20 mt-2">
              <div className="flex flex-col gap-4">
                <p className="text-xs">
                  By logging in, you agree to our{" "}
                  <a
                    href="https://www.ternstone.digital"
                    className="text-[#dc9b28] hover:underline transition-all"
                    target="_blank"
                  >
                    terms & conditions
                  </a>{" "}
                  and{" "}
                  <a
                    href="https://www.ternstone.digital"
                    className="text-[#dc9b28] hover:underline transition-all"
                    target="_blank"
                  >
                    privacy policy
                  </a>
                </p>
                <p className="text-xs">
                  Copyright &copy; {year} Ternstone Digital Limited All Rights
                  Reserved
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
